/**
 * Shared helpers for the People area: day maths on 'YYYY-MM-DD' strings,
 * roster classification (employment type bucket, SCHADS band, hours) and a
 * per-day memo so the heavy generators (training records, payroll) run once
 * per day rather than once per request.
 */
import { DATA_START, Quarter, now, ymd } from '../../clock';
import { rng } from '../../rng';
import { SITES, STAFF, Staff } from '../../world';

export const DAY_MS = 86400000;

export type TypeKey = 'fulltime' | 'parttime' | 'casual' | 'other' | 'unknown';
export type BandKey = 'dsw' | 'corporate' | 'unknown';
export type ByType<T> = Record<TypeKey, T>;

export const TYPE_KEYS: TypeKey[] = ['fulltime', 'parttime', 'casual', 'other', 'unknown'];

/** Local-noon Date for a 'YYYY-MM-DD' string (noon avoids DST edge cases). */
export function parseDay(s: string): Date {
    const [y, m, d] = s.split('-').map(Number);
    return new Date(y, m - 1, d, 12);
}

export function today(): string {
    return ymd(now());
}

export function shiftDay(s: string, days: number): string {
    const d = parseDay(s);
    d.setDate(d.getDate() + days);
    return ymd(d);
}

export function shiftMonths(s: string, months: number): string {
    const d = parseDay(s);
    const day = d.getDate();
    d.setDate(1);
    d.setMonth(d.getMonth() + months);
    const last = new Date(d.getFullYear(), d.getMonth() + 1, 0).getDate();
    d.setDate(Math.min(day, last));
    return ymd(d);
}

/** Whole days from a to b (b - a). */
export function daysBetween(a: string, b: string): number {
    return Math.round((parseDay(b).getTime() - parseDay(a).getTime()) / DAY_MS);
}

export const DATA_START_DAY = ymd(DATA_START);

export const round1 = (n: number): number => Math.round(n * 10) / 10;
export const round2 = (n: number): number => Math.round(n * 100) / 100;
export const pct1 = (num: number, den: number): number => (den > 0 ? round1((num / den) * 100) : 0);
export const pctInt = (num: number, den: number): number => (den > 0 ? Math.round((num / den) * 100) : 0);

export function emptyByType(): ByType<number> {
    return { fulltime: 0, parttime: 0, casual: 0, other: 0, unknown: 0 };
}

export function typeOf(s: Staff): TypeKey {
    return s.employmentType === 'Full Time' ? 'fulltime' : s.employmentType === 'Part Time' ? 'parttime' : 'casual';
}

export function ehTypeLabel(s: Staff): string {
    return s.employmentType === 'Full Time' ? 'Full-time' : s.employmentType === 'Part Time' ? 'Part-time' : 'Casual';
}

export function levelNum(s: Staff): number {
    return Number(s.level.replace(/\D/g, '')) || 0;
}

/** SCHADS band: DSW = Level ≤ 3, Corporate/Mgmt/Clinical = Level ≥ 4. */
export function bandOf(s: Staff): BandKey {
    const n = levelNum(s);
    return n === 0 ? 'unknown' : n <= 3 ? 'dsw' : 'corporate';
}

/** Pay point within the level, by tenure (1 per year, max 4). */
export function payPoint(s: Staff, asOf = today()): number {
    return Math.min(4, 1 + Math.floor(Math.max(0, daysBetween(s.startDate, asOf)) / 365));
}

/** Contracted weekly hours; casuals have none. */
export function standardHours(s: Staff): number | null {
    if (s.employmentType === 'Full Time') {
        return 38;
    }
    if (s.employmentType === 'Part Time') {
        return rng(`people:hours:${s.id}`).pick([15.2, 19, 22.8, 26.6, 30.4]);
    }
    return null;
}

/** Typical weekly hours actually worked (casuals included). */
export function typicalHours(s: Staff): number {
    return standardHours(s) ?? rng(`people:casualhours:${s.id}`).int(8, 26);
}

export const HEAD_OFFICE = SITES[0].name;

/** Delivers services directly to clients/communities (everyone outside Corporate Services). */
export const isFrontline = (s: Staff): boolean => s.program !== 'Corporate Services';
/** Personal care roles: Disability and Aged Care. */
export const isCare = (s: Staff): boolean => s.program === 'Disability Services (NDIS)' || s.program === 'Aged Care';
/** Based out bush — drives remote roads routinely. */
export const isRemote = (s: Staff): boolean => s.site !== HEAD_OFFICE;

/** Employed on the given day (started on/before, not yet finished). */
export function isActiveOn(s: Staff, day: string): boolean {
    return s.startDate <= day && (!s.endDate || s.endDate > day);
}

export function activeOn(day: string): Staff[] {
    return STAFF.filter(s => isActiveOn(s, day));
}

export function activeToday(): Staff[] {
    return activeOn(today());
}

/** Tenure in whole days at `asOf` (or at the end date for leavers). */
export function tenureDays(s: Staff, asOf = today()): number {
    return Math.max(0, daysBetween(s.startDate, s.endDate && s.endDate < asOf ? s.endDate : asOf));
}

/** Months 'YYYY-MM' from DATA_START to the current month, ascending. */
export function monthKeys(): string[] {
    const out: string[] = [];
    const end = today().slice(0, 7);
    let d = new Date(DATA_START.getFullYear(), DATA_START.getMonth(), 1, 12);
    for (let guard = 0; guard < 120; guard++) {
        const k = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        out.push(k);
        if (k >= end) {
            break;
        }
        d = new Date(d.getFullYear(), d.getMonth() + 1, 1, 12);
    }
    return out;
}

export function lastDayOfMonth(month: string): string {
    const [y, m] = month.split('-').map(Number);
    return ymd(new Date(y, m, 0, 12));
}

/** Pseudo-UUID for EH ids, stable per seed. */
export function uuidFor(seed: string): string {
    const r = rng(`uuid:${seed}`);
    const hex = (n: number) => Array.from({ length: n }, () => r.int(0, 15).toString(16)).join('');
    return `${hex(8)}-${hex(4)}-4${hex(3)}-${['8', '9', 'a', 'b'][r.int(0, 3)]}${hex(3)}-${hex(12)}`;
}

export const ehId = (s: Staff): string => uuidFor(`staff:${s.id}`);

export interface TrendWindow {
    days: number | null;
    quarter: string | null;
    range: { from: string; to: string } | null;
    dates: string[];
}

/**
 * Daily rows for /api/trends/*: the selected quarter's days up to today, or
 * the last `days` days (clamped 7–365) — never before the data window opens.
 */
export function trendWindow(q: Quarter | null, daysParam: string | null): TrendWindow {
    const t = today();
    let from: string;
    let to: string;
    let days: number | null = null;
    if (q) {
        from = ymd(q.start);
        to = ymd(q.end);
    } else {
        days = Math.min(365, Math.max(7, Number(daysParam) || 90));
        from = shiftDay(t, -(days - 1));
        to = t;
    }
    const dates: string[] = [];
    let d = from < DATA_START_DAY ? DATA_START_DAY : from;
    const last = to > t ? t : to;
    for (let guard = 0; d <= last && guard < 400; guard++) {
        dates.push(d);
        d = shiftDay(d, 1);
    }
    return { days, quarter: q ? q.key : null, range: q ? { from: ymd(q.start), to: ymd(q.end) } : null, dates };
}

const memo = new Map<string, unknown>();

/** Cache a pure generator for the rest of the (local) day. */
export function daily<T>(key: string, fn: () => T): T {
    const k = `${key}@${today()}`;
    if (!memo.has(k)) {
        memo.set(k, fn());
    }
    return memo.get(k) as T;
}

/** Deep copy for handing a cached structure to a caller that may trim it. */
export function clone<T>(v: T): T {
    return JSON.parse(JSON.stringify(v));
}
